import { useEffect } from 'react';
import { ChevronRight, RefreshCw, AlertTriangle } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { getSeriesColor, getSeriesCompletion, sortSeriesByLatest } from '../utils/seriesColors';

const SeriesGrid = ({ onSeriesSelect }) => {
  const {
    allSets,
    loading,
    serverOnline,
    loadAllSets,
    checkServerHealth,
    getSeriesGroups,
    packStats,
    collection,
  } = useCollection();

  // Load sets when component mounts or server comes online
  useEffect(() => {
    if (serverOnline && allSets.length === 0) {
      loadAllSets().catch(console.error);
    }
  }, [serverOnline, allSets.length, loadAllSets]);

  // Health check on mount, then poll — gates the initial set load above.
  useEffect(() => {
    checkServerHealth();
  }, [checkServerHealth]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkServerHealth();
    }, 10000);
    return () => clearInterval(interval);
  }, [checkServerHealth]);

  const seriesGroups = getSeriesGroups();
  const seriesList = sortSeriesByLatest(seriesGroups);

  const handleRetryLoad = () => {
    loadAllSets().catch(console.error);
  };

  if (loading && seriesList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy-dark border-t-transparent mb-4"></div>
        <h3 className="font-heading font-bold text-lg text-ink-primary mb-1">Loading Pokemon Series</h3>
        <p className="text-ink-secondary text-sm">Fetching series data from Pokemon TCG API...</p>
      </div>
    );
  }

  if (!loading && seriesList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-cream-card border border-border flex items-center justify-center mb-4">
          <AlertTriangle size={22} className="text-ink-muted" />
        </div>
        <h3 className="font-heading font-bold text-lg text-ink-primary mb-2">No Pokemon Series Loaded</h3>
        <p className="text-ink-secondary text-sm mb-6 max-w-xs">
          {!serverOnline
            ? 'Server is offline. Start your local server to load card data.'
            : 'Click below to load Pokemon series data.'}
        </p>
        {serverOnline && (
          <button
            onClick={handleRetryLoad}
            disabled={loading}
            className="flex items-center gap-2 bg-navy-dark text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy-light transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Load Pokemon Series'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 md:px-10 py-6">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-2xl text-ink-primary mb-1">Pokemon Series Collection</h2>
        <p className="text-ink-secondary text-sm">
          Explore {seriesList.length} different Pokemon series • {allSets.length} total sets available
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {seriesList.map((series) => {
          const seriesSets = seriesGroups[series];
          const latestSet = seriesSets[0];
          const rawPacks = packStats?.seriesStats?.[series] || {};
          const packs = rawPacks.packs || 0;
          const hits = rawPacks.hits || 0;
          const hitRate = packs > 0 ? Math.round((hits / packs) * 100) : 0;
          const completion = getSeriesCompletion(seriesSets, collection);
          const color = getSeriesColor(series);

          return (
            <div key={series}>
              {/* Mobile: condensed row */}
              <button
                onClick={() => onSeriesSelect(series)}
                className="md:hidden w-full text-left bg-white border border-border rounded-2xl overflow-hidden mb-3"
              >
                <div
                  className="px-4 py-3 flex items-center justify-between"
                  style={{ backgroundColor: color }}
                >
                  <div>
                    <div className="font-heading font-bold text-white text-[15px]">{series}</div>
                    {latestSet && (
                      <div className="text-white/55 text-[11px] mt-0.5">
                        {new Date(latestSet.releaseDate).getFullYear()}
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] font-medium text-white bg-white/20 px-2 py-0.5 rounded-full">
                    {seriesSets.length} sets
                  </span>
                </div>
                {latestSet && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-[13px] text-ink-secondary">
                      Latest: <span className="text-ink-primary font-medium">{latestSet.name}</span>
                    </span>
                    <ChevronRight size={16} className="text-ink-faint" />
                  </div>
                )}
              </button>

              {/* Desktop: full card */}
              <button
                onClick={() => onSeriesSelect(series)}
                className="hidden md:flex md:flex-col text-left w-full bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-shadow"
              >
                <div className="px-5 py-4" style={{ backgroundColor: color }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-heading font-bold text-white text-lg">{series}</div>
                      {latestSet && (
                        <div className="text-white/55 text-xs mt-0.5">
                          {new Date(latestSet.releaseDate).getFullYear()}
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] font-medium text-white bg-white/20 px-2.5 py-1 rounded-full">
                      {seriesSets.length} sets
                    </span>
                  </div>
                </div>

                <div className="px-5 py-4 flex-1">
                  {latestSet && (
                    <div className="mb-4">
                      <div className="text-[10px] uppercase tracking-wide text-ink-muted mb-1">Latest</div>
                      <div className="text-[14px] font-bold text-ink-primary">{latestSet.name}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-cream-bg rounded-lg py-2 text-center">
                      <div className="font-heading font-bold text-ink-primary text-sm">{packs}</div>
                      <div className="text-[10px] text-ink-muted uppercase tracking-wide mt-0.5">Packs</div>
                    </div>
                    <div className="bg-cream-bg rounded-lg py-2 text-center">
                      <div className="font-heading font-bold text-ink-primary text-sm">{hitRate}%</div>
                      <div className="text-[10px] text-ink-muted uppercase tracking-wide mt-0.5">Hit%</div>
                    </div>
                    <div className="bg-cream-bg rounded-lg py-2 text-center">
                      <div className="font-heading font-bold text-ink-primary text-sm">{completion.percentage}%</div>
                      <div className="text-[10px] text-ink-muted uppercase tracking-wide mt-0.5">Done</div>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {!serverOnline && (
        <div className="mt-8 max-w-2xl mx-auto bg-danger-bg border border-danger-border rounded-xl p-5">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20} className="text-brick flex-shrink-0" />
            <div>
              <h4 className="font-heading font-bold text-brick text-sm mb-1">Server Offline</h4>
              <p className="text-brick/80 text-sm">
                Start your local server with <code className="bg-white/60 px-1.5 py-0.5 rounded text-xs">npm run server</code> to load real Pokemon card data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeriesGrid;
